import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import API_URL from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const UserDetails = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    email: '',
    billingAddress: {
      street: '',
      city: '',
      postalCode: '',
      country: '',
    },
    vatNumber: '',
    personalNumber: '',
  });
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_URL}/api/userProfile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.dateOfBirth) {
            data.dateOfBirth = new Date(data.dateOfBirth).toISOString().split('T')[0];
          }
          setFormData(data);
        }
      } catch (error) {
        setError('Nepodařilo se načíst uživatelská data');
        console.error('Chyba při načítání profilu:', error);
      }
    };

    fetchUserProfile();
  }, [currentUser]);

  const validateForm = () => {
    const errors = [];
    
    if (!formData.firstName) errors.push('Jméno je povinné');
    if (!formData.lastName) errors.push('Příjmení je povinné');
    if (!formData.dateOfBirth) errors.push('Datum narození je povinné');
    if (!formData.phoneNumber) errors.push('Telefon je povinný');
    if (!formData.email) errors.push('Email je povinný');
    if (!formData.billingAddress.street) errors.push('Ulice je povinná');
    if (!formData.billingAddress.city) errors.push('Město je povinné');
    if (!formData.billingAddress.postalCode) errors.push('PSČ je povinné');
    if (!formData.billingAddress.country) errors.push('Země je povinná');

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      setLoading(false);
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/userProfile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          dateOfBirth: new Date(formData.dateOfBirth).toISOString()
        })
      });

      if (response.ok) {
        setSuccessMessage('Údaje byly úspěšně uloženy');
        setTimeout(() => {
          navigate('/my-account');
        }, 2000);
      } else {
        const data = await response.json();
        setError(data.message || 'Nastala chyba při ukládání dat');
      }
    } catch (error) {
      setError('Nepodařilo se uložit změny');
      console.error('Chyba při ukládání profilu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-6 sm:py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-4 sm:p-8">
        <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center">Osobní údaje</h2>
        
        {error && (
          <div className="mb-4 p-4 text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 text-green-700 bg-green-100 rounded-md">
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Jméno</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 
                         focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Příjmení</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Datum narození</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Telefon</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <h3 className="text-xl font-bold mt-6 mb-4 col-span-2">Fakturační údaje</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700">Ulice</label>
              <input
                type="text"
                name="billingAddress.street"
                value={formData.billingAddress.street}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Město</label>
              <input
                type="text"
                name="billingAddress.city"
                value={formData.billingAddress.city}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">PSČ</label>
              <input
                type="text"
                name="billingAddress.postalCode"
                value={formData.billingAddress.postalCode}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Země</label>
              <input
                type="text"
                name="billingAddress.country"
                value={formData.billingAddress.country}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">IČO (nepovinné)</label>
              <input
                type="text"
                name="vatNumber"
                value={formData.vatNumber}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Rodné číslo (nepovinné)</label>
              <input
                type="text"
                name="personalNumber"
                value={formData.personalNumber}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full sm:w-auto sm:px-8 bg-blue-500 text-white py-2 px-4 
                       rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 
                       focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base
                       ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Ukládám...' : 'Uložit údaje'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserDetails;
